// Edicao da ficha botanica. So os campos alterados sao enviados (PUT parcial): o que o
// tecnico nao tocou continua como estava, inclusive campos que esta tela nao mostra.
import { useMemo, useState } from "react";
import type { CodeLabels, SpeciesProfileCatalogs, SpeciesProfileFields } from "@/types/species-profile";
import { MONTHS } from "../profileFormat";

type Kind = "text" | "line" | "number" | "select" | "multi" | "months" | "bool" | "list";

interface FieldDef {
  key: keyof SpeciesProfileFields;
  name: string;
  kind: Kind;
  catalog?: keyof SpeciesProfileCatalogs;
  unit?: string;
  hint?: string;
}

const GROUPS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "Identificação",
    fields: [
      { key: "description", name: "Descrição", kind: "text" },
      { key: "life_form", name: "Forma de vida", kind: "select", catalog: "life_forms" },
      { key: "other_popular_names", name: "Outros nomes populares", kind: "list", hint: "Separe por vírgula" },
      { key: "synonyms", name: "Sinônimos (nomes científicos)", kind: "list", hint: "Separe por vírgula" },
      { key: "native_biomes", name: "Biomas de ocorrência", kind: "multi", catalog: "biomes" },
      { key: "natural_distribution", name: "Distribuição natural", kind: "text" },
    ],
  },
  {
    title: "Porte e crescimento",
    fields: [
      { key: "adult_height_min_m", name: "Altura adulta mínima", kind: "number", unit: "m" },
      { key: "adult_height_max_m", name: "Altura adulta máxima", kind: "number", unit: "m" },
      { key: "crown_diameter_max_m", name: "Diâmetro máximo da copa", kind: "number", unit: "m" },
      { key: "growth_rate", name: "Crescimento", kind: "select", catalog: "growth_rates" },
      { key: "longevity", name: "Longevidade", kind: "select", catalog: "longevity" },
      { key: "foliage", name: "Folhagem", kind: "select", catalog: "foliage" },
    ],
  },
  {
    title: "Fenologia",
    fields: [
      { key: "flowering_months", name: "Meses de floração", kind: "months" },
      { key: "fruiting_months", name: "Meses de frutificação", kind: "months" },
      { key: "flower_color", name: "Cor da flor", kind: "line" },
      { key: "fruit_type", name: "Tipo de fruto", kind: "line" },
      { key: "edible_fruit", name: "Fruto comestível", kind: "bool" },
      { key: "pollination_dispersal", name: "Polinização e dispersão", kind: "text" },
    ],
  },
  {
    title: "Estrutura",
    fields: [
      { key: "root_system", name: "Sistema radicular", kind: "select", catalog: "root_systems" },
      { key: "root_aggressiveness", name: "Agressividade da raiz", kind: "select", catalog: "levels" },
      { key: "wood_strength", name: "Resistência da madeira", kind: "select", catalog: "levels" },
      { key: "branch_drop_tendency", name: "Tendência à queda de galhos", kind: "select", catalog: "levels" },
    ],
  },
  {
    title: "Adequação ao ambiente urbano",
    fields: [
      { key: "fit_narrow_sidewalk", name: "Calçada estreita", kind: "select", catalog: "fit" },
      { key: "fit_wide_sidewalk", name: "Calçada larga", kind: "select", catalog: "fit" },
      { key: "fit_under_power_lines", name: "Sob fiação", kind: "select", catalog: "fit" },
      { key: "fit_median_strip", name: "Canteiro central", kind: "select", catalog: "fit" },
      { key: "fit_parks_squares", name: "Praças e parques", kind: "select", catalog: "fit" },
      { key: "min_permeable_area_m2", name: "Área permeável mínima", kind: "number", unit: "m²" },
      { key: "min_distance_building_m", name: "Distância mínima de edificação", kind: "number", unit: "m" },
      { key: "urban_fit_notes", name: "Observações de uso urbano", kind: "text" },
    ],
  },
  {
    title: "Benefícios e problemas",
    fields: [
      { key: "benefit_codes", name: "Benefícios", kind: "multi", catalog: "benefits" },
      { key: "benefits", name: "Benefícios (detalhe)", kind: "text" },
      { key: "problem_codes", name: "Problemas", kind: "multi", catalog: "problems" },
      { key: "problems", name: "Problemas (detalhe)", kind: "text" },
      { key: "toxicity", name: "Toxicidade", kind: "text" },
    ],
  },
  {
    title: "Manejo",
    fields: [
      { key: "planting", name: "Plantio", kind: "text" },
      { key: "pruning", name: "Poda", kind: "text" },
      { key: "pruning_season", name: "Época de poda", kind: "line" },
      { key: "pruning_tolerance", name: "Tolerância à poda", kind: "select", catalog: "levels" },
      { key: "transplant_tolerance", name: "Tolerância ao transplante", kind: "select", catalog: "levels" },
      { key: "irrigation", name: "Irrigação", kind: "text" },
      { key: "fertilization", name: "Adubação", kind: "text" },
      { key: "seedling_production", name: "Produção de mudas", kind: "text" },
      { key: "management_notes", name: "Observações de manejo", kind: "text" },
    ],
  },
  {
    title: "Conservação e legislação",
    fields: [
      { key: "conservation_status", name: "Grau de ameaça", kind: "select", catalog: "conservation" },
      { key: "cutting_restricted", name: "Corte restrito", kind: "bool" },
      { key: "legal_protection", name: "Proteção legal", kind: "text" },
    ],
  },
];

type Values = Partial<Record<keyof SpeciesProfileFields, unknown>>;

const EMPTY: Record<Kind, unknown> = {
  text: null, line: null, number: null, select: null, multi: [], months: [], bool: null, list: [],
};

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const inputCls =
  "w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none";

interface Props {
  initial: SpeciesProfileFields | null;
  catalogs: SpeciesProfileCatalogs;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (changes: Partial<SpeciesProfileFields>, note: string) => void;
}

export default function ProfileForm({ initial, catalogs, saving, error, onCancel, onSave }: Props) {
  const start = useMemo<Values>(() => {
    const v: Values = {};
    for (const g of GROUPS) for (const f of g.fields) v[f.key] = initial?.[f.key] ?? EMPTY[f.kind];
    return v;
  }, [initial]);
  const [values, setValues] = useState<Values>(start);
  // Texto livre da lista ("a, b") fica como digitado ate sair do campo.
  const [listText, setListText] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      GROUPS.flatMap((g) => g.fields)
        .filter((f) => f.kind === "list")
        .map((f) => [f.key, ((start[f.key] as string[]) ?? []).join(", ")])
    )
  );
  const [note, setNote] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const set = (key: keyof SpeciesProfileFields, value: unknown) => setValues((v) => ({ ...v, [key]: value }));

  const changes = useMemo(() => {
    const out: Values = {};
    for (const key of Object.keys(values) as (keyof SpeciesProfileFields)[]) {
      if (!same(values[key], start[key])) out[key] = values[key];
    }
    return out;
  }, [values, start]);
  const changedCount = Object.keys(changes).length;

  function submit() {
    const min = values.adult_height_min_m as number | null;
    const max = values.adult_height_max_m as number | null;
    if (min !== null && max !== null && min > max) {
      setLocalError("A altura mínima não pode ser maior que a máxima.");
      return;
    }
    setLocalError(null);
    onSave(changes as Partial<SpeciesProfileFields>, note.trim());
  }

  function renderInput(f: FieldDef) {
    const v = values[f.key];
    const labels: CodeLabels = f.catalog ? catalogs[f.catalog] : {};
    const id = `f-${f.key}`;
    switch (f.kind) {
      case "text":
        return (
          <textarea id={id} rows={3} className={inputCls} value={(v as string) ?? ""}
            onChange={(e) => set(f.key, e.target.value === "" ? null : e.target.value)} />
        );
      case "line":
        return (
          <input id={id} type="text" className={inputCls} value={(v as string) ?? ""}
            onChange={(e) => set(f.key, e.target.value === "" ? null : e.target.value)} />
        );
      case "number":
        return (
          <div className="flex items-center gap-2">
            <input id={id} type="number" inputMode="decimal" min={0} step="0.1" className={inputCls}
              value={v === null || v === undefined ? "" : String(v)}
              onChange={(e) => set(f.key, e.target.value === "" ? null : Number(e.target.value))} />
            {f.unit && <span className="text-xs text-foreground-500">{f.unit}</span>}
          </div>
        );
      case "select":
        return (
          <select id={id} className={inputCls} value={(v as string) ?? ""}
            onChange={(e) => set(f.key, e.target.value || null)}>
            <option value="">Não informado</option>
            {Object.entries(labels).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        );
      case "bool":
        return (
          <select id={id} className={inputCls} value={v === null || v === undefined ? "" : v ? "1" : "0"}
            onChange={(e) => set(f.key, e.target.value === "" ? null : e.target.value === "1")}>
            <option value="">Não informado</option>
            <option value="1">Sim</option>
            <option value="0">Não</option>
          </select>
        );
      case "multi": {
        const selected = (v as string[]) ?? [];
        return (
          <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={`${id}-label`}>
            {Object.entries(labels).map(([code, name]) => {
              const on = selected.includes(code);
              return (
                <button key={code} type="button" aria-pressed={on}
                  onClick={() => set(f.key, on ? selected.filter((c) => c !== code) : [...selected, code])}
                  className={`badge border cursor-pointer ${on ? "bg-primary-500 text-white border-primary-500" : "bg-white text-foreground-600 border-background-300 hover:bg-background-100"}`}>
                  {name}
                </button>
              );
            })}
          </div>
        );
      }
      case "months": {
        const selected = (v as number[]) ?? [];
        return (
          <div className="grid grid-cols-6 sm:grid-cols-12 gap-1" role="group" aria-labelledby={`${id}-label`}>
            {MONTHS.map((m, i) => {
              const on = selected.includes(i + 1);
              return (
                <button key={m} type="button" aria-pressed={on}
                  onClick={() => set(f.key, on ? selected.filter((x) => x !== i + 1) : [...selected, i + 1].sort((a, b) => a - b))}
                  className={`py-1.5 rounded text-xs border ${on ? "bg-primary-500 text-white border-primary-500" : "bg-white text-foreground-600 border-background-300 hover:bg-background-100"}`}>
                  {m}
                </button>
              );
            })}
          </div>
        );
      }
      case "list":
        return (
          <input id={id} type="text" className={inputCls} value={listText[f.key] ?? ""}
            onChange={(e) => {
              setListText((t) => ({ ...t, [f.key]: e.target.value }));
              set(f.key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean));
            }} />
        );
    }
  }

  return (
    <div className="space-y-4">
      {GROUPS.map((g) => (
        <section key={g.title} className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="text-sm font-semibold text-foreground-900 mb-4">{g.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.fields.map((f) => {
              const wide = f.kind === "text" || f.kind === "multi" || f.kind === "months";
              const changed = f.key in changes;
              return (
                <div key={f.key} className={wide ? "md:col-span-2" : undefined}>
                  <label id={`f-${f.key}-label`} htmlFor={`f-${f.key}`} className="flex items-center gap-2 text-xs font-medium text-foreground-600 mb-1">
                    {f.name}
                    {changed && <span className="badge bg-accent-50 text-accent-700 text-[10px]">alterado</span>}
                    {f.hint && <span className="font-normal text-foreground-400">{f.hint}</span>}
                  </label>
                  {renderInput(f)}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div className="sticky bottom-0 bg-white/95 backdrop-blur border border-background-200 rounded-xl p-4 shadow-sm space-y-3">
        <div>
          <label htmlFor="change-note" className="text-xs font-medium text-foreground-600">
            O que foi alterado e com base em quê (opcional)
          </label>
          <input id="change-note" type="text" maxLength={500} className={`${inputCls} mt-1`} value={note}
            placeholder="Ex.: altura conferida no manual da AMMA" onChange={(e) => setNote(e.target.value)} />
        </div>
        {(localError || error) && (
          <p className="text-sm text-danger-700" role="alert"><i className="ri-error-warning-line mr-1" />{localError || error}</p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-foreground-500">
            {changedCount === 0 ? "Nenhuma alteração ainda." : `${changedCount} campo(s) alterado(s). Ao salvar, a ficha volta para revisão.`}
          </span>
          <div className="flex gap-2">
            <button type="button" className="btn-outline" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button type="button" className="btn-primary disabled:opacity-50" disabled={saving || changedCount === 0} onClick={submit}>
              {saving ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
